import React from 'react';
import {
  AlertBanner,
  TabSwitcher,
  ProductSelectionModal,
  DetailsModal,
} from './WarehouseDocumentBase';
import { useExternalIssueData } from './issue/useExternalIssueData';
import CustomerSection from './issue/CustomerSection';
import CustomerSelectModal from './issue/CustomerSelectModal';
import ProductsList from './issue/ProductsList';
import IssueHistory from './issue/IssueHistory';
import IssueDetailsContent from './issue/IssueDetailsContent';

const PRODUCT_MODAL_PROPS = {
  gradient: 'linear-gradient(135deg, var(--info), var(--purple))',
  spinnerColor: 'var(--info)',
  addButtonColor: 'var(--info)',
  addButtonHoverColor: 'var(--info)',
  icon: '📦',
  title: 'Wybierz produkt do wydania',
  subtitle: 'WZ - Wydanie zewnętrzne',
  warehouseLabel: '🏪 Magazyn źródłowy',
  hintBg: 'var(--info-light)',
  hintColor: 'var(--info)',
  hintIcon: 'ℹ️',
  hintText: 'Wydanie zewnętrzne zmniejsza stan magazynowy. Upewnij się, że wybrałeś właściwego odbiorcę.',
};

const HISTORY_TABS = [
  { key: 'new', label: 'Nowe wydanie', iconEmoji: '➕', activeColor: 'var(--info)' },
];

const ExternalIssue = () => {
  const {
    searchTerm, setSearchTerm, loading, error, setError, success, setSuccess,
    showProductModal, setShowProductModal, selectedCustomer,
    showCustomerModal, setShowCustomerModal,
    showAddCustomerModal, setShowAddCustomerModal,
    customerSearch, setCustomerSearch, notes, setNotes,
    warehouses, selectedWarehouse, setSelectedWarehouse,
    products, loadingProducts, selectedProducts,
    updateProductQuantity, updateProductReason, removeProduct,
    issues, activeTab, setActiveTab,
    dateFilter, setDateFilter, statusFilter, setStatusFilter,
    showDetailsModal, setShowDetailsModal, selectedIssue, setSelectedIssue, loadingDetails,
    filteredCustomers, calculateTotal, addProduct,
    handleSelectCustomer, handleAddNewCustomer, clearSelectedCustomer,
    getCustomerDisplayName, getCustomerAddress,
    submitExternalIssue, showIssueDetails, printIssue, loadIssues,
    documentDate, setDocumentDate,
  } = useExternalIssueData();

  const tabs = [
    ...HISTORY_TABS,
    { key: 'history', label: `Historia WZ (${issues.length})`, iconEmoji: '📋', activeColor: 'var(--info)' },
  ];

  return (
    <div style={{ fontSize: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: '600', fontSize: '13px' }}>
          📦 Wydanie Zewnętrzne (WZ)
        </span>
      </div>

      <AlertBanner type="error" message={error} onClose={() => setError('')} />
      <AlertBanner type="success" message={success} onClose={() => setSuccess('')} />
      <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />

      {activeTab === 'new' && (
        <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', border: '1px solid var(--border)', padding: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {warehouses.length > 1 && (
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>
                Magazyn źródłowy:
              </label>
              <select
                value={selectedWarehouse || ''}
                onChange={(e) => setSelectedWarehouse(parseInt(e.target.value))}
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              >
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.nazwa}</option>)}
              </select>
            </div>
          )}

          <CustomerSection
            selectedCustomer={selectedCustomer}
            getCustomerDisplayName={getCustomerDisplayName}
            getCustomerAddress={getCustomerAddress}
            onOpenModal={() => setShowCustomerModal(true)}
            onClear={clearSelectedCustomer}
          />

          {/* Data dokumentu WZ */}
          <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>Data dokumentu:</label>
            <input
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
              style={{
                padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)',
                borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
              }}
            />
          </div>

          <ProductsList
            selectedWarehouse={selectedWarehouse}
            selectedProducts={selectedProducts}
            onAddClick={() => setShowProductModal(true)}
            updateQuantity={updateProductQuantity}
            updateReason={updateProductReason}
            removeProduct={removeProduct}
            calculateTotal={calculateTotal}
            notes={notes}
            onNotesChange={setNotes}
            loading={loading}
            onSubmit={submitExternalIssue}
          />
        </div>
      )}

      {activeTab === 'history' && (
        <IssueHistory
          issues={issues}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={loadIssues}
          onShowDetails={showIssueDetails}
          onPrint={printIssue}
        />
      )}

      <ProductSelectionModal
        show={showProductModal}
        onClose={() => { setShowProductModal(false); setSearchTerm(''); }}
        products={products}
        loadingProducts={loadingProducts}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedProducts={selectedProducts}
        onAddProduct={addProduct}
        warehouses={warehouses}
        selectedWarehouse={selectedWarehouse}
        onWarehouseChange={setSelectedWarehouse}
        {...PRODUCT_MODAL_PROPS}
      />

      <DetailsModal
        show={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setSelectedIssue(null); }}
        title={`📦 Szczegóły WZ: ${selectedIssue?.document_number || ''}`}
        headerBg="var(--info)"
      >
        {loadingDetails
          ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie szczegółów...</div>
          : <IssueDetailsContent issue={selectedIssue} onPrint={printIssue} />}
      </DetailsModal>

      <CustomerSelectModal
        show={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        customerSearch={customerSearch}
        onSearchChange={setCustomerSearch}
        filteredCustomers={filteredCustomers}
        onSelectCustomer={handleSelectCustomer}
        onOpenAddCustomer={() => { setShowCustomerModal(false); setShowAddCustomerModal(true); }}
        showAddCustomerModal={showAddCustomerModal}
        onCloseAddCustomer={() => setShowAddCustomerModal(false)}
        onAddNewCustomer={handleAddNewCustomer}
      />
    </div>
  );
};

export default ExternalIssue;
